import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import Modal from '../Modal';

const INVOICE_TYPES = ['MEDICINE', 'SERVICE', 'DELIVERY'];
const NON_MEDICINE_TYPES = ['SERVICE', 'DELIVERY', 'COD_CHARGES'];

const PAYMENT_STATUSES = [
    'DRAFT', 'GENERATED', 'PARTIALLY_GENERATED', 'OVERDUE_GENERATED',
    'PAID', 'PARTIALLY_PAID', 'OVERDUE', 'CANCELLED', 'REFUNDED', 'PARTIALLY_REFUNDED',
];

const round2 = (val) => Math.round((val || 0) * 100) / 100;

const parseNum = (val) => (val === '' || val === null || val === undefined) ? null : parseFloat(val) || 0;

// Preserve the inter-state (IGST) vs intra-state (CGST+SGST) character of the existing split when
// recomputing amounts. An item already split as IGST stays IGST; otherwise it's CGST + SGST.
// The backend remains authoritative (it re-splits from the order's address on save).
const nonMedSplit = (prev, taxable, gst) => {
    const inter = (parseNum(prev.igst) || 0) > 0 && !(parseNum(prev.cgst) || 0) && !(parseNum(prev.sgst) || 0);
    const half = round2(taxable * 0.09);
    return inter ? { sgst: 0, cgst: 0, igst: gst } : { sgst: half, cgst: half, igst: 0 };
};

const EditPaymentItemModal = ({ paymentItem, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        invoiceType: 'SERVICE',
        subTotal: '',
        discount: '',
        discountPercentage: '',
        gstPercentage: 0,
        gst: 0,
        cgst: 0,
        igst: 0,
        sgst: '',
        cessAmount: 0,
        cessPercentage: 0,
        totalAmount: '',
        status: 'DRAFT',
        notes: '',
        refEntityType: '',
        refEntityValue: '',
    });

    useEffect(() => {
        if (paymentItem) {
            setFormData({
                invoiceType: paymentItem.invoiceType || 'SERVICE',
                subTotal: paymentItem.subTotal ?? '',
                discount: paymentItem.discount ?? '',
                discountPercentage: paymentItem.discountPercentage ?? '',
                gstPercentage: paymentItem.gstPercentage ?? 0,
                gst: paymentItem.gst ?? 0,
                cgst: paymentItem.cgst ?? 0,
                igst: paymentItem.igst ?? 0,
                sgst: paymentItem.sgst ?? '',
                cessAmount: paymentItem.cessAmount ?? 0,
                cessPercentage: paymentItem.cessPercentage ?? 0,
                totalAmount: paymentItem.totalAmount ?? '',
                status: paymentItem.status ?? 'DRAFT',
                notes: paymentItem.notes ?? '',
                refEntityType: paymentItem.refEntityType ?? '',
                refEntityValue: paymentItem.refEntityValue ?? '',
            });
        }
    }, [paymentItem]);

    const isNonMedicine = NON_MEDICINE_TYPES.includes(formData.invoiceType);

    // Non-medicine items carry a fixed 18% GST, shown intra-state (SGST 9% + CGST 9%) here.
    // The backend re-evaluates the split from the order's address on save / order creation.
    // Switching TO a non-medicine type: enforce 18% + intra split and (re)compute the total.
    // Read from `prev` (not the closure) so the loaded payment-item values are used on mount.
    useEffect(() => {
        if (!isNonMedicine) return;
        setFormData((prev) => {
            const taxable = (parseNum(prev.subTotal) || 0) - (parseNum(prev.discount) || 0);
            const gst = round2(taxable * 0.18);
            const cess = parseNum(prev.cessAmount) || 0;
            return {
                ...prev,
                gstPercentage: 18,
                gst,
                ...nonMedSplit(prev, taxable, gst),
                totalAmount: Math.round(taxable + gst + cess), // nearest rupee (GST round-off)
            };
        });
    }, [formData.invoiceType]);

    // Keep the 18% split in sync as subtotal/discount change — WITHOUT touching the Total field
    // (the operator may be typing into Total, which back-derives the subtotal).
    useEffect(() => {
        if (!isNonMedicine) return;
        setFormData((prev) => {
            const taxable = (parseNum(prev.subTotal) || 0) - (parseNum(prev.discount) || 0);
            const gst = round2(taxable * 0.18);
            return { ...prev, gstPercentage: 18, gst, ...nonMedSplit(prev, taxable, gst) };
        });
    }, [formData.subTotal, formData.discount]);

    const computeGstFromTaxableAmount = (taxableAmount, gstPercentage, sgstVal) => {
        if (sgstVal !== null && sgstVal !== undefined && sgstVal !== '' && sgstVal > 0) {
            return { gst: sgstVal, cgst: 0, igst: 0 };
        }
        if (gstPercentage > 0 && taxableAmount > 0) {
            const gst = round2(taxableAmount * (gstPercentage / 100));
            return { gst, cgst: round2(gst / 2), igst: round2(gst / 2) };
        }
        return { gst: 0, cgst: 0, igst: 0 };
    };

    const getDiscount = (next) => {
        const subTotal = parseNum(next.subTotal) || 0;
        const discountVal = parseNum(next.discount);
        const discountPctVal = parseNum(next.discountPercentage);
        if (discountVal !== null && discountVal !== undefined && discountVal !== '') {
            return discountVal;
        }
        if (discountPctVal !== null && discountPctVal !== undefined && discountPctVal !== '' && subTotal > 0) {
            return round2(subTotal * (discountPctVal / 100));
        }
        return 0;
    };

    const handleChange = (field, value) => {
        setFormData((prev) => {
            const next = { ...prev, [field]: value };
            const discount = getDiscount(next);
            const gstPercentage = parseNum(next.gstPercentage) || 0;
            // For non-medicine, SGST is auto-computed (intra-state half), NOT a manual override —
            // so it must not trigger the "SGST entered" special-case in the GST math below.
            const sgstVal = NON_MEDICINE_TYPES.includes(next.invoiceType) ? null : parseNum(next.sgst);
            const cessAmount = parseNum(next.cessAmount) || 0;

            if (field === 'discount') {
                const subTotal = parseNum(next.subTotal) || 0;
                const discountVal = parseNum(value);
                if (subTotal > 0 && discountVal !== null && discountVal !== undefined && discountVal !== '') {
                    next.discountPercentage = round2((discountVal / subTotal) * 100);
                } else if (value === '' || value === null || value === undefined) {
                    next.discountPercentage = '';
                }
            } else if (field === 'discountPercentage') {
                const subTotal = parseNum(next.subTotal) || 0;
                const discountPctVal = parseNum(value);
                if (subTotal > 0 && discountPctVal !== null && discountPctVal !== undefined && discountPctVal !== '') {
                    next.discount = round2(subTotal * (discountPctVal / 100));
                } else if (value === '' || value === null || value === undefined) {
                    next.discount = '';
                }
            }

            if (field === 'subTotal') {
                const subTotal = parseNum(value) || 0;
                const pct = parseNum(next.discountPercentage);
                if (subTotal > 0 && pct !== null && pct !== undefined && pct !== '') {
                    next.discount = round2(subTotal * (pct / 100));
                }
                const effectiveDiscount = getDiscount(next);
                const taxable = subTotal - effectiveDiscount;
                const { gst, cgst, igst } = computeGstFromTaxableAmount(taxable, gstPercentage, sgstVal);
                next.gst = gst;
                next.cgst = cgst;
                next.igst = igst;
                next.totalAmount = subTotal > 0 ? round2(subTotal - effectiveDiscount + gst + cessAmount) : '';
            } else if (field === 'totalAmount') {
                const totalAmount = parseNum(value) || 0;
                if (totalAmount > 0) {
                    let newSubTotal;
                    const discountPct = parseNum(next.discountPercentage);
                    const useDiscountPct = discountPct !== null && discountPct !== undefined && discountPct !== '' && discountPct > 0;
                    if (sgstVal !== null && sgstVal !== undefined && sgstVal !== '' && sgstVal > 0) {
                        newSubTotal = round2(totalAmount + discount - sgstVal - cessAmount);
                    } else if (gstPercentage > 0 && useDiscountPct) {
                        newSubTotal = round2((totalAmount - cessAmount) / ((1 - discountPct / 100) * (1 + gstPercentage / 100)));
                    } else if (gstPercentage > 0) {
                        newSubTotal = round2((totalAmount - cessAmount) / (1 + gstPercentage / 100) + discount);
                    } else {
                        const { gst } = computeGstFromTaxableAmount(0, gstPercentage, sgstVal);
                        newSubTotal = round2(totalAmount + discount - gst - cessAmount);
                    }
                    next.subTotal = newSubTotal;
                    const newDiscount = useDiscountPct ? round2(newSubTotal * (discountPct / 100)) : discount;
                    if (useDiscountPct) next.discount = newDiscount;
                    const newTaxable = newSubTotal - newDiscount;
                    const { gst, cgst, igst } = computeGstFromTaxableAmount(newTaxable, gstPercentage, sgstVal);
                    next.gst = gst;
                    next.cgst = cgst;
                    next.igst = igst;
                } else {
                    next.subTotal = '';
                    next.gst = 0;
                    next.cgst = 0;
                    next.igst = 0;
                }
            } else if (field === 'gstPercentage' || field === 'sgst') {
                const subTotal = parseNum(next.subTotal) || 0;
                const taxable = subTotal - discount;
                const { gst, cgst, igst } = computeGstFromTaxableAmount(taxable, gstPercentage, sgstVal);
                next.gst = gst;
                next.cgst = cgst;
                next.igst = igst;
                if (subTotal > 0) {
                    next.totalAmount = round2(subTotal - discount + gst + cessAmount);
                }
            } else if (field === 'discount' || field === 'discountPercentage' || field === 'cessAmount' || field === 'cessPercentage') {
                const subTotal = parseNum(next.subTotal) || 0;
                const newDiscount = getDiscount(next);
                const newCess = parseNum(next.cessAmount) || 0;
                const taxable = subTotal - newDiscount;
                const { gst } = computeGstFromTaxableAmount(taxable, gstPercentage, sgstVal);
                if (subTotal > 0) {
                    next.totalAmount = round2(subTotal - newDiscount + gst + newCess);
                }
            }

            // Non-medicine: round the COMPUTED total to nearest rupee (GST round-off). Don't round
            // the operator's direct entry in the Total field.
            if (field !== 'totalAmount' && NON_MEDICINE_TYPES.includes(next.invoiceType)
                && next.totalAmount !== '' && next.totalAmount !== null && next.totalAmount !== undefined) {
                next.totalAmount = Math.round(parseNum(next.totalAmount) || 0);
            }
            return next;
        });
    };

    const handleSave = () => {
        const subTotal = parseNum(formData.subTotal) || 0;
        const totalAmount = parseNum(formData.totalAmount) || 0;
        const discount = getDiscount(formData);
        const discountPercentage = parseNum(formData.discountPercentage);
        const gstPercentage = parseInt(formData.gstPercentage, 10) || 0;
        const gst = parseNum(formData.gst) || 0;
        const cgst = parseNum(formData.cgst) || 0;
        const igst = parseNum(formData.igst) || 0;
        const sgst = parseNum(formData.sgst) || 0;
        const cessAmount = parseNum(formData.cessAmount) || 0;
        const cessPercentage = parseNum(formData.cessPercentage) || 0;

        const payload = {
            invoiceType: formData.invoiceType,
            subTotal,
            discountPercentage: discountPercentage !== null && discountPercentage !== undefined ? discountPercentage : 0,
            discount,
            gstPercentage,
            gst,
            cgst,
            igst,
            sgst,
            cessAmount,
            cessPercentage,
            totalAmount,
            status: formData.status,
            notes: formData.notes || '',
            refEntityType: formData.refEntityType || '',
            refEntityValue: formData.refEntityValue || '',
        };
        onSave(payload);
        onClose();
    };

    const subTotal = parseNum(formData.subTotal);
    const totalAmount = parseNum(formData.totalAmount);
    const canSave = paymentItem && subTotal > 0 && totalAmount > 0;

    if (!paymentItem) return null;

    return (
        <Modal title={`Edit Payment Item #${paymentItem.id}`} onClose={onClose}>
            <div className="flex min-h-0 flex-col">
            <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ID</label>
                        <input
                            type="text"
                            value={paymentItem.id || ''}
                            readOnly
                            className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50 text-gray-600"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Order ID</label>
                        <input
                            type="text"
                            value={paymentItem.orderId || ''}
                            readOnly
                            className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50 text-gray-600"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Type</label>
                    <select
                        value={formData.invoiceType}
                        onChange={(e) => handleChange('invoiceType', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        {INVOICE_TYPES.map((t) => (
                            <option key={t} value={t}>{t}</option>
                        ))}
                    </select>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Ref Entity Type</label>
                        <select
                            value={formData.refEntityType}
                            onChange={(e) => handleChange('refEntityType', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">-- none --</option>
                            <option value="inventoryId">inventoryId</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Ref Entity Value</label>
                        <input
                            type="text"
                            value={formData.refEntityValue}
                            onChange={(e) => handleChange('refEntityValue', e.target.value)}
                            placeholder="e.g. eVital inventoryId"
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtotal</label>
                    <input
                        type="number"
                        step="0.01"
                        value={formData.subTotal}
                        onChange={(e) => handleChange('subTotal', e.target.value)}
                        placeholder="0.00"
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Discount (amount)</label>
                        <input
                            type="number"
                            step="0.01"
                            value={formData.discount}
                            onChange={(e) => handleChange('discount', e.target.value)}
                            placeholder="0.00"
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Discount (%)</label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={formData.discountPercentage}
                            onChange={(e) => handleChange('discountPercentage', e.target.value)}
                            placeholder="0.00"
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GST Percentage (%)</label>
                    <input
                        type="number"
                        step="1"
                        min="0"
                        value={formData.gstPercentage}
                        onChange={(e) => handleChange('gstPercentage', e.target.value)}
                        readOnly={isNonMedicine}
                        title={isNonMedicine ? 'Fixed 18% for non-medicine items' : undefined}
                        placeholder="0"
                        className={`w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isNonMedicine ? 'bg-gray-50 text-gray-600' : ''}`}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GST</label>
                    <input
                        type="number"
                        step="0.01"
                        value={formData.gst}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50 text-gray-600"
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">CGST (GST/2)</label>
                        <input
                            type="number"
                            step="0.01"
                            value={formData.cgst}
                            readOnly
                            className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50 text-gray-600"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">SGST (GST/2)</label>
                        <input
                            type="number"
                            step="0.01"
                            value={formData.sgst}
                            onChange={(e) => handleChange('sgst', e.target.value)}
                            readOnly={isNonMedicine}
                            placeholder="0.00"
                            className={`w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isNonMedicine ? 'bg-gray-50 text-gray-600' : ''}`}
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">IGST</label>
                    <input
                        type="number"
                        step="0.01"
                        value={formData.igst}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50 text-gray-600"
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Cess Amount</label>
                        <input
                            type="number"
                            step="0.01"
                            value={formData.cessAmount}
                            onChange={(e) => handleChange('cessAmount', e.target.value)}
                            placeholder="0.00"
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Cess %</label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.cessPercentage}
                            onChange={(e) => handleChange('cessPercentage', e.target.value)}
                            placeholder="0.00"
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                        value={formData.status}
                        onChange={(e) => setFormData((p) => ({ ...p, status: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        {PAYMENT_STATUSES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total (calculated)</label>
                    <input
                        type="number"
                        step="0.01"
                        value={formData.totalAmount}
                        onChange={(e) => handleChange('totalAmount', e.target.value)}
                        placeholder="0.00"
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
                        placeholder="Optional notes"
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>

            </div>
                <div className="sticky bottom-0 z-10 mt-4 flex shrink-0 justify-end gap-3 border-t border-gray-200 bg-white-50 pt-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={!canSave}
                        className="px-4 py-2 bg-blue-600 text-white-50 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Update
                    </button>
                </div>
            </div>
        </Modal>
    );
};

EditPaymentItemModal.propTypes = {
    paymentItem: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
};

export default EditPaymentItemModal;
